import * as React from 'react';

import {
  DocumentType,
  enumSalesOrderSortOption,
  enumSalesOrderStatus,
  SalesOrderSortOption,
} from '@rematter/frontend-lib-api-client';
import {
  Badge,
  Button,
  DataGrid,
  FloatingToolbar,
  ListScreenLayout,
  Pagination,
  useMemoDataGridRows,
  type PaginationProps,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  LucidePlusCircle,
  LucidePrinter,
  LucideTrash2,
  LucideX,
} from 'lucide-react';

import { $remoteFacilities } from '../../_state/facilities.state';
import * as state from '../../_state/salesOrdersList.state';
import { createSalesOrdersVoidValidationFetcher } from '../../_state/salesOrdersList.state';
import { usePrintWithBrowser } from '../../hooks/usePrintWithBrowser';
import { useTranslations } from '../../i18n/useTranslations';
import { tradeRoutePaths } from '../../routes';
import { SalesOrderRecord } from '../../types/salesOrder';
import { enumEntityType, VoidEntityDialog } from '../_shared/VoidEntityDialog';
import { LoadCreate } from '../loads/LoadCreate';

import { SalesOrderWizard } from './_SalesOrderWizard';
import { useSalesOrdersListColumns } from './hooks/useSalesOrdersListColumns';
import { SalesOrderFilters } from './SalesOrderListFilters';
import { SalesOrderMarkAsClosedDialog } from './SalesOrderMarkAsClosedDialog';

import { $resetSalesOrderForm } from '~/modules/trade/_state/salesOrderForm.state';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

const COLUMN_TO_SORT_MAP: Record<
  string,
  {
    asc: SalesOrderSortOption;
    desc: SalesOrderSortOption;
  }
> = {
  documentNumber: {
    asc: enumSalesOrderSortOption.DocumentNumberAsc,
    desc: enumSalesOrderSortOption.DocumentNumberDesc,
  },
  customer: {
    asc: enumSalesOrderSortOption.AccountAsc,
    desc: enumSalesOrderSortOption.AccountDesc,
  },
  status: {
    asc: enumSalesOrderSortOption.StatusAsc,
    desc: enumSalesOrderSortOption.StatusDesc,
  },
  endDate: {
    asc: enumSalesOrderSortOption.EndDateAsc,
    desc: enumSalesOrderSortOption.EndDateDesc,
  },
  createdOn: {
    asc: enumSalesOrderSortOption.CreatedAtAsc,
    desc: enumSalesOrderSortOption.CreatedAtDesc,
  },
};

//
// ⚛️ ⚛️ ⚛️ SALES ORDERS LIST VIEW ⚛️ ⚛️ ⚛️
//
export const SalesOrderListView = () => {
  const t = useTranslations();
  const [, navigate] = useModuleLocation();
  const [, resetSalesOrderForm] = useAtom($resetSalesOrderForm);

  // Atoms
  const [selectedKeys, setSelectedKeys] = useAtom(state.$selectedKeys);
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const [isWizardOpen, setIsWizardOpen] = useAtom(state.$isWizardOpen);
  const [draftSalesOrderId, setDraftSalesOrderId] = useAtom(
    state.$draftSalesOrderId,
  );
  const [isLoadDialogOpen, setIsLoadDialogOpen] = useAtom(
    state.$isLoadDialogOpen,
  );
  const [selectedSalesOrder, setSelectedSalesOrder] = useAtom(
    state.$selectedSalesOrder,
  );
  const [isVoidDialogOpen, setIsVoidDialogOpen] = useAtom(
    state.$isVoidDialogOpen,
  );
  const [isCloseDialogOpen, setIsCloseDialogOpen] = useAtom(
    state.$isCloseDialogOpen,
  );
  const setCurrentSorting = useSetAtom(state.$currentSorting);
  const [bulkVoidSalesOrders, setBulkVoidSalesOrders] = useAtom(
    state.$bulkVoidSalesOrders,
  );
  const bulkVoidSalesOrderMutation = useSetAtom(state.$bulkVoidSalesOrder);
  const voidSalesOrder = useSetAtom(state.$voidSalesOrder);
  const updateSalesOrder = useSetAtom(state.$updateSalesOrder);
  const { data, isFetching, loadingState, error, refetch } = useAtomValue(
    state.$remoteSalesOrders,
  );
  const { data: facilitiesData } = useAtomValue($remoteFacilities);

  const toast = useToast();
  const { print, isPrinting } = usePrintWithBrowser();

  const salesOrders = data?.salesOrders ?? [];
  const total = data?.totalCount ?? 0;
  const selectedRows = useMemoDataGridRows(salesOrders, selectedKeys);
  const facilities = facilitiesData?.facilities ?? [];

  // Memoized values
  const fetchSalesOrdersVoidValidation = React.useMemo(
    () => createSalesOrdersVoidValidationFetcher(bulkVoidSalesOrderMutation),
    [bulkVoidSalesOrderMutation],
  );

  // Callbacks
  const handleRefetchSalesOrders = React.useCallback(() => {
    refetch();
  }, [refetch]);

  const handlePaginationChange = React.useCallback<
    NonNullable<PaginationProps['onChange']>
  >(
    ({ limit: newLimit, offset: newOffset }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );

  const handleSalesOrderClick = React.useCallback(
    (salesOrder: SalesOrderRecord) => {
      navigate(tradeRoutePaths.salesOrderDetail, {
        params: { id: salesOrder.id },
      });
    },
    [navigate],
  );

  const handleCreateClick = React.useCallback(() => {
    resetSalesOrderForm();
    setDraftSalesOrderId(null);
    setIsWizardOpen(true);
  }, [resetSalesOrderForm, setDraftSalesOrderId, setIsWizardOpen]);

  const handleWizardSuccess = React.useCallback(() => {
    setIsWizardOpen(false);
    setDraftSalesOrderId(null);
    refetch();
    toast.success(t.salesOrdersView.wizard.messages.createSuccess);
  }, [t, toast, setDraftSalesOrderId, setIsWizardOpen, refetch]);

  const handleWizardOpenChange = React.useCallback(
    (open: boolean) => {
      setIsWizardOpen(open);
      if (!open) {
        setDraftSalesOrderId(null);
      }
    },
    [setDraftSalesOrderId, setIsWizardOpen],
  );

  const handleSetIsLoadDialogOpen = React.useCallback(
    (open: boolean, salesOrderId?: string) => {
      if (open) {
        setSelectedSalesOrder(
          salesOrderId ? ({ id: salesOrderId } as SalesOrderRecord) : null,
        );
      }
      setIsLoadDialogOpen(open);
    },
    [setSelectedSalesOrder, setIsLoadDialogOpen],
  );

  const handlePrintSelected = React.useCallback(async () => {
    if (selectedRows.length === 0) {
      return;
    }

    try {
      const documentsToPrint: Array<{
        documentType: DocumentType;
        salesOrderId: string;
      }> = [];

      selectedRows.forEach((salesOrder) => {
        if (salesOrder.id) {
          documentsToPrint.push({
            documentType: 'SalesOrder' as DocumentType,
            salesOrderId: salesOrder.id,
          });
        }
      });

      if (documentsToPrint.length === 0) {
        return;
      }

      await print({
        documents: documentsToPrint,
      });

      setSelectedKeys(new Set());
    } catch (error) {
      console.error('Failed to print sales orders:', error);
    }
  }, [selectedRows, print, setSelectedKeys]);

  const handleClearSelection = React.useCallback(() => {
    setSelectedKeys(new Set());
  }, [setSelectedKeys]);

  const commonCellProps = React.useCallback(
    (salesOrder: SalesOrderRecord) => ({
      className: 'cursor-pointer',
      onDoubleClick: () => handleSalesOrderClick(salesOrder),
    }),
    [handleSalesOrderClick],
  );

  const handleBulkVoid = React.useCallback(() => {
    const salesOrdersToVoid = selectedRows
      .filter((row) => row.id && row.documentNumber !== undefined)
      .map((row) => ({
        id: row.id as string,
        displayNumber: String(row.documentNumber),
        status: row.status,
      }));

    if (salesOrdersToVoid.length > 0) {
      setBulkVoidSalesOrders(salesOrdersToVoid);
      setIsVoidDialogOpen(true);
    }
  }, [selectedRows, setBulkVoidSalesOrders, setIsVoidDialogOpen]);

  const handleBulkVoidComplete = React.useCallback(() => {
    const salesOrderNumbers = bulkVoidSalesOrders
      .map((so) => `#${so.displayNumber}`)
      .join(', ');
    toast.success(
      t.salesOrdersView.messages.bulkVoidSuccess(
        bulkVoidSalesOrders.length,
        salesOrderNumbers,
      ),
    );
    setSelectedKeys(new Set());
    setIsVoidDialogOpen(false);
    setBulkVoidSalesOrders([]);
    handleRefetchSalesOrders();
  }, [
    toast,
    bulkVoidSalesOrders,
    setSelectedKeys,
    setIsVoidDialogOpen,
    setBulkVoidSalesOrders,
    handleRefetchSalesOrders,
  ]);

  const handleBulkVoidCancel = React.useCallback(() => {
    setIsVoidDialogOpen(false);
    setBulkVoidSalesOrders([]);
  }, [setIsVoidDialogOpen, setBulkVoidSalesOrders]);

  const handleVoidSalesOrder = React.useCallback(
    (salesOrderId: string) => {
      const salesOrder = salesOrders.find((so) => so.id === salesOrderId);
      if (!salesOrder) return;

      setBulkVoidSalesOrders([
        {
          id: salesOrder.id as string,
          displayNumber: String(salesOrder.documentNumber),
          status: salesOrder.status,
        },
      ]);
      setIsVoidDialogOpen(true);
    },
    [salesOrders, setBulkVoidSalesOrders, setIsVoidDialogOpen],
  );

  const handleVoidConfirm = React.useCallback(
    async (id: string, forceUnlink: boolean) => {
      try {
        await voidSalesOrder({ id, forceUnlink });
      } catch (error) {
        toast.error(
          `Failed to void sales order: ${error instanceof Error ? error.message : 'Unknown error'}`,
        );
        throw error;
      }
    },
    [voidSalesOrder, toast],
  );

  const handleCloseSalesOrder = React.useCallback(
    (salesOrderId: string) => {
      const salesOrderToClose = salesOrders.find(
        (salesOrder) => salesOrder.id === salesOrderId,
      );
      if (salesOrderToClose == null) {
        return;
      }
      setSelectedSalesOrder(salesOrderToClose);
      setIsCloseDialogOpen(true);
    },
    [salesOrders, setSelectedSalesOrder, setIsCloseDialogOpen],
  );

  const handleCloseSalesOrderConfirm = React.useCallback(async () => {
    const salesOrderId = selectedSalesOrder?.id;
    if (salesOrderId == null) {
      return;
    }
    try {
      await updateSalesOrder({
        id: salesOrderId,
        status: enumSalesOrderStatus.Finished,
      });
      toast.success(t.salesOrderDetail.messages.finished);
      setSelectedSalesOrder(null);
      handleRefetchSalesOrders();
    } catch (error) {
      toast.error(
        `Failed to close sales order: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    }
  }, [
    selectedSalesOrder,
    updateSalesOrder,
    t,
    toast,
    handleRefetchSalesOrders,
    setSelectedSalesOrder,
  ]);

  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting([enumSalesOrderSortOption.CreatedAtDesc]);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setCurrentSorting([sortOption]);
        }
      }
      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );

  const handleCloseLoadDialog = React.useCallback(() => {
    setIsLoadDialogOpen(false);
    setSelectedSalesOrder(null);
  }, [setIsLoadDialogOpen, setSelectedSalesOrder]);

  const handleSuccessLoadDialog = React.useCallback(() => {
    setIsLoadDialogOpen(false);
  }, [setIsLoadDialogOpen]);

  const columns = useSalesOrdersListColumns({
    facilities,
    commonCellProps,
    setIsLoadDialogOpen: handleSetIsLoadDialogOpen,
    setSelectedSalesOrder,
    onVoid: handleVoidSalesOrder,
    onClose: handleCloseSalesOrder,
    onRefetch: handleRefetchSalesOrders,
  });

  const emptyPanelProps = {
    title: t.salesOrdersView.emptyState.title,
    description: (
      <span>
        {t.salesOrdersView.emptyState.subtitle(
          <strong>{t.salesOrdersView.emptyState.action}</strong>,
        )}
      </span>
    ),
    action: (
      <Button
        onPress={handleCreateClick}
        variant="solid"
        color="primary"
        icon={<LucidePlusCircle />}
      >
        {t.salesOrdersView.toolbar.create}
      </Button>
    ),
    centered: true,
  };

  return (
    <div className="@SalesOrderListView flex h-full flex-col">
      <ListScreenLayout
        filters={<SalesOrderFilters />}
        actions={
          <Button
            onPress={handleCreateClick}
            variant="solid"
            color="primary"
            icon={<LucidePlusCircle />}
          >
            {t.salesOrdersView.toolbar.create}
          </Button>
        }
        footer={
          total > limit ? (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          ) : undefined
        }
      >
        <DataGrid<SalesOrderRecord>
          isLoading={loadingState.isInitialLoading || isFetching}
          columns={columns}
          dataSource={salesOrders}
          rowKey="id"
          aria-label={t.salesOrdersView.table.aria}
          selectionMode="multiple"
          selectedKeys={selectedKeys}
          onSelectionChange={setSelectedKeys}
          onColumnSort={handleColumnSort}
          className="w-full text-sm"
          fullWidth
          persistColumnOrderKey="trade:sales-order-list"
          hasError={!!error}
          errorPanelProps={
            error
              ? {
                  description: t.salesOrdersView.content.error(error.message),
                  centered: true,
                }
              : undefined
          }
          emptyPanelProps={emptyPanelProps}
        />
      </ListScreenLayout>

      <FloatingToolbar visible={selectedRows.length > 0}>
        <div className="flex flex-row items-center justify-between gap-3">
          <div className="flex flex-row items-center gap-2 text-sm">
            <Badge color="primary" size="small">
              {selectedRows.length}
            </Badge>{' '}
            {t.salesOrdersView.toolbar.selected}
          </div>
          <div className="flex flex-row items-center gap-2">
            <Button
              variant="outline"
              icon={<LucidePrinter />}
              isDisabled={selectedRows.length < 1 || isPrinting}
              onPress={handlePrintSelected}
            >
              {t.salesOrdersView.toolbar.print}
            </Button>
            <Button
              variant="outline"
              icon={<LucideTrash2 />}
              isDisabled={selectedRows.length < 1}
              onPress={handleBulkVoid}
            >
              {t.salesOrdersView.toolbar.delete(selectedRows.length)}
            </Button>
            <Button
              variant="text"
              aria-label="Clear selection"
              onPress={handleClearSelection}
              icon={<LucideX />}
            />
          </div>
        </div>
      </FloatingToolbar>

      <SalesOrderWizard
        isOpen={isWizardOpen}
        onOpenChange={handleWizardOpenChange}
        onSuccess={handleWizardSuccess}
        draftSalesOrderId={draftSalesOrderId ?? undefined}
      />
      <LoadCreate
        isOpen={isLoadDialogOpen}
        requiredSalesOrderId={selectedSalesOrder?.id}
        onClose={handleCloseLoadDialog}
        onSuccess={handleSuccessLoadDialog}
      />
      {selectedSalesOrder && isCloseDialogOpen && (
        <SalesOrderMarkAsClosedDialog
          onConfirm={handleCloseSalesOrderConfirm}
          onCancel={() => {
            setSelectedSalesOrder(null);
            setIsCloseDialogOpen(false);
          }}
        />
      )}
      {isVoidDialogOpen && bulkVoidSalesOrders.length > 0 && (
        <VoidEntityDialog
          entityType={enumEntityType.salesOrder}
          entities={bulkVoidSalesOrders}
          onVoid={handleVoidConfirm}
          fetchValidation={fetchSalesOrdersVoidValidation}
          onComplete={handleBulkVoidComplete}
          onCancel={handleBulkVoidCancel}
        />
      )}
    </div>
  );
};
